import { Service, computed, inject } from '@angular/core';
import type { AccionesRol } from 'modelos';
import { SesionStore } from '../sesion/sesion.store';
import { VISTAS, type ModuloFaro, type Requisito, type VistaFaro } from './vistas';

/**
 * Qué puede hacer el usuario.
 *
 * Reemplaza a la parte de permisos de `HelperService` (1.918 líneas en IRIX,
 * ADR-006). Acá sólo hay permisos: ni rutas de otras verticales, ni
 * notificaciones, ni formateo.
 *
 * Replica la semántica de IRIX (`HelperService.tienePermiso` y `tieneModulo`):
 *
 * - **nivel 0** (el cliente del permiso activo tiene `nivel === 0`) ve y hace
 *   todo, sin mirar roles ni módulos;
 * - una **acción** se tiene si está en alguno de los roles del permiso activo;
 *   con varias pedidas, alcanza una;
 * - un **módulo** se tiene si lo habilita el permiso (`permiso.modulos`) **y**
 *   el cliente lo tiene activo en su configuración.
 *
 * Qué pide cada pantalla está en `VISTAS` (`./vistas.ts`); `puedeVer` la
 * consulta y es lo que usan el menú, los guards, el buscador y los enlaces.
 *
 * **Esto no es seguridad.** La autorización real la hace el backend con los
 * decoradores `@Acciones` de `gestion-api-gestion`. Esto es usabilidad: que no
 * se muestre un botón que va a devolver 403.
 */
@Service()
export class PermisosService {
  private readonly store = inject(SesionStore);

  /** Un cliente de nivel 0 ve absolutamente todo. Mismo criterio que IRIX. */
  readonly esNivel0 = computed(() => this.store.cliente()?.nivel === 0);

  /**
   * Si se ofrece el filtro por cliente de los listados. Como IRIX: un cliente
   * Final no tiene hijos por los que filtrar, y sin `Administración - Ver
   * clientes` el selector no tendría de dónde sacar la lista.
   */
  readonly filtraPorCliente = computed(
    () =>
      this.store.clientePropio()?.tipoCliente !== 'Final' &&
      this.tienePermiso('Administración - Ver clientes'),
  );

  /** Todas las acciones del permiso activo, aplanadas de sus roles. */
  private readonly accionesDelUsuario = computed(() => {
    const roles = this.store.permisoActual()?.rols ?? [];
    const acciones = new Set<string>();
    for (const rol of roles) {
      for (const accion of rol.acciones ?? []) acciones.add(accion);
    }
    return acciones;
  });

  /** `true` si tiene **alguna** de las acciones pedidas. */
  tienePermiso(...acciones: AccionesRol[]): boolean {
    if (this.esNivel0()) return true;
    const mias = this.accionesDelUsuario();
    return acciones.some((accion) => mias.has(accion));
  }

  /**
   * `true` si tiene **alguno** de los módulos pedidos: habilitado en el permiso
   * y activo en el cliente. Es `tieneModulo` de IRIX, con sólo los módulos que
   * FARO usa.
   */
  tieneModulo(...modulos: ModuloFaro[]): boolean {
    if (this.esNivel0()) return true;
    const permiso = this.store.permisoActual();
    const delPermiso = permiso?.modulos;
    // IRIX lee `permiso.cliente.config`; si el permiso no vino poblado, el del
    // cliente de la sesión es el mismo.
    const config = permiso?.cliente?.config ?? this.store.cliente()?.config;
    return modulos.some((modulo) => {
      switch (modulo) {
        case 'Luminarias':
          return !!delPermiso?.moduloLuminarias && !!config?.moduloLuminarias?.activo;
        case 'Logs':
          return !!delPermiso?.moduloLogs && !!config?.moduloLogs?.activo;
        case 'Servicio Técnico':
          return !!delPermiso?.moduloEventosTecnicos && !!config?.moduloEventosTecnicos?.activo;
        case 'Administración':
          return !!delPermiso?.moduloAdministracion && !!config?.moduloAdministracion?.activo;
      }
    });
  }

  /** Si se cumplen **todas** las condiciones del requisito. */
  cumple(requisito: Requisito): boolean {
    if (requisito.nivel0 && !this.esNivel0()) return false;
    if (requisito.usaPuestas && !this.store.usaPuestas()) return false;
    if (requisito.modulo && !this.tieneModulo(requisito.modulo)) return false;
    if (requisito.acciones?.length && !this.tienePermiso(...requisito.acciones)) return false;
    return true;
  }

  /** Si el operador puede entrar a esa pantalla (ver `VISTAS`). */
  puedeVer(vista: VistaFaro): boolean {
    return this.cumple(VISTAS[vista]);
  }

  /** Versión reactiva, para usar en plantillas sin recalcular en cada ciclo. */
  puede(...acciones: AccionesRol[]) {
    return computed(() => this.tienePermiso(...acciones));
  }
}
